import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Select,
  Show
} from "shared/components";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { type PropertyFeeFrequency } from "shared/types";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { SectionCard } from "shared/app/SectionCard";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { useFormTyped } from "shared/hooks";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";
import { Row } from "views/applications-id-report/components/Row";
import addressApi from "api/landlord/address";
import { Property, type PropertyFee } from "types/landlord/address";
import { getErrorMessage } from "utils/error";
import {
  PROPERTY_FEE_FREQUENCY_OPTIONS,
  PROPERTY_FEE_LABEL,
  applyFrequencyDefault,
  buildInitialFeeDrafts,
  buildPropertyFeesPayload,
  effectiveDraftAmount,
  formatFeeAmount,
  formatPropertyFee,
  isChargedAmount,
  normalizePropertyFees,
  type PropertyFeeDraft,
  type PropertyFeeDrafts
} from "../lib/propertyFees";

// Up to $9,999,999.99 — keeps the integer cents well inside the API's column.
const feeAmountParser = moneyCentsParser({ maxDigits: 9, trimZeroCents: true });

const FeeField = ({
  fee,
  draft,
  onChange
}: {
  fee: PropertyFee;
  draft: PropertyFeeDraft;
  onChange: (draft: PropertyFeeDraft) => void;
}) => {
  const synced = fee.pms_mapped && draft.pms_synced;
  const amount = effectiveDraftAmount(fee, draft);
  const hasAmount = isChargedAmount(amount);
  // The edit view puts "per pet" in the label; the read view puts it after the amount.
  const label =
    fee.key === "pet_fee"
      ? `${PROPERTY_FEE_LABEL[fee.key]} (per pet)`
      : PROPERTY_FEE_LABEL[fee.key];

  // Every change re-applies the Billed default: a new amount starts at Monthly,
  // a cleared one drops its frequency.
  const update = (changes: Partial<PropertyFeeDraft>) =>
    onChange(applyFrequencyDefault(fee, { ...draft, ...changes }));

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="money-input-2">
          <BaseInput
            id={`fee-${fee.key}-amount`}
            label={label}
            prefix="$"
            placeholder={synced ? "-" : "0.00"}
            parser={feeAmountParser}
            readOnly={synced}
            className={synced ? "w-full [&_.bg-white]:!bg-alpha-40" : "w-full"}
            value={amount}
            onChange={(cents) => update({ manual_amount_cents: cents })}
            hideOptional
          />
          <Show if={fee.pms_mapped}>
            <PmsSyncOption
              name={`fee-${fee.key}-sync`}
              value={draft.pms_synced}
              onChange={(nextSynced) => update({ pms_synced: nextSynced })}
            />
          </Show>
        </div>
        {/* Select renders its label and control as siblings, so it needs its own grid cell. */}
        <div>
          <Select
            id={`fee-${fee.key}-frequency`}
            label="Billed"
            placeholder="Enter an amount first"
            options={PROPERTY_FEE_FREQUENCY_OPTIONS}
            value={hasAmount ? draft.frequency : null}
            isDisabled={!hasAmount}
            onChange={(e) =>
              update({ frequency: e.target.value as PropertyFeeFrequency })
            }
            hideOptional
          />
        </div>
      </div>
      <Show if={fee.pms_mapped}>
        <PMSValue
          value={
            isChargedAmount(fee.pms_amount_cents)
              ? formatFeeAmount(fee.pms_amount_cents)
              : undefined
          }
        />
      </Show>
    </div>
  );
};

const EditFeesForm = ({
  propertyId,
  fees,
  onClose
}: {
  propertyId: number;
  fees: PropertyFee[];
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { register, values, onSubmit } = useFormTyped<PropertyFeeDrafts>(
    buildInitialFeeDrafts(fees)
  );

  const { mutate: updateFees, isPending } = useMutation({
    mutationFn: (drafts: PropertyFeeDrafts) =>
      addressApi.editProperty({
        propertyId,
        fees: buildPropertyFeesPayload(fees, drafts)
      }),
    onSuccess: () => {
      toast.success("Fees updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update fees: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (drafts) => {
      // Nothing changed — skip the round trip.
      if (buildPropertyFeesPayload(fees, drafts)) updateFees(drafts);
      else onClose();
    }
  });

  return (
    <SectionCard
      title="Fees"
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    >
      <div className="space-y-6">
        {fees.map((fee) => (
          <FeeField
            key={fee.key}
            fee={fee}
            draft={values[fee.key]}
            onChange={(draft) => register(fee.key).onChange(draft)}
          />
        ))}
      </div>
    </SectionCard>
  );
};

export const FeesSection = ({ property }: { property: Property }) => {
  const { canEditPropertyDetails, propertyEditDeniedTooltip } =
    useEditDetailsPermissions();
  const [isEditing, setIsEditing] = useState(false);
  const fees = useMemo(
    () => normalizePropertyFees(property.fees),
    [property.fees]
  );

  if (isEditing)
    return (
      <EditFeesForm
        propertyId={property.id}
        fees={fees}
        onClose={() => setIsEditing(false)}
      />
    );

  return (
    <SectionCard
      title="Fees"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
          disabled={!canEditPropertyDetails}
          tooltip={propertyEditDeniedTooltip}
          tooltipPlacement="bottom-end"
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        {fees.map((fee) => (
          <Row
            key={fee.key}
            title={PROPERTY_FEE_LABEL[fee.key]}
            value={formatPropertyFee(fee)}
            valueClassName="w-[500px]"
          />
        ))}
      </div>
    </SectionCard>
  );
};
